import { Paper } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import SVGInline from 'react-svg-inline';
import { useExternalCurrencyRequestsQuery } from '../../hooks/useExternalCurrency';
import { AllDone } from './AllDone';
import ExternalCurrencyModal from './modals/ExternalCurrencyModal';
import { getDaysDifference } from './utils';
import WidgetListItem from './WidgetListItem';

const checkCircleIcon = require('../../../images/check-circle.svg.raw');

const ExternalCurrencyWidget = ({ institute, user }) => {
  const [externalCurrencyModalOpen, setExternalCurrencyModalOpen] =
    React.useState(false);
  const [selectedExternalCurrency, setSelectedExternalCurrency] =
    React.useState(null);

  const { data: externalCurrencyRequests, isPending } =
    useExternalCurrencyRequestsQuery();

  const externalCurrencyRequestsAsWidgetFormat = React.useMemo(() => {
    if (!externalCurrencyRequests || externalCurrencyRequests?.length === 0)
      return [];

    return externalCurrencyRequests.map((request) => {
      const overdueDays = getDaysDifference(request.submitted_at || request.created_at)

      return {
        id: request.id,
        name: request.user?.full_name || 'Unknown User',
        createdAt: request.submitted_at || request.created_at,
        overdueDays: overdueDays > 0 ? overdueDays : 0,
        currencyTypeLabel:
          request.type === 'ReaPointRequest'
            ? 'Professional'
            : request.type === 'VocationalPointRequest'
            ? 'Vocational'
            : '',
        ...request
      };
    });
  }, [externalCurrencyRequests]);

  const overdueCount = React.useMemo(() => {
    const threshold = institute?.point_request_warning_days;

    return externalCurrencyRequestsAsWidgetFormat.filter(
      (p) => threshold && (p.overdueDays || 0) >= threshold
    ).length;
  }, [institute, externalCurrencyRequestsAsWidgetFormat]);

  const totalCount = externalCurrencyRequests?.length;

  const onItemClick = (externalCurrency) => {
    setExternalCurrencyModalOpen(true);
    setSelectedExternalCurrency(externalCurrency);
  };

  const handleCloseExternalCurrencyModal = () => {
    setExternalCurrencyModalOpen(false);
    setSelectedExternalCurrency(null);
  };

  return (
    <>
      <Paper
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            borderBottom: '1px solid #eee'
          }}
        >
          <Typography
            variant="h6"
            style={{ marginRight: 12, fontSize: 16, fontWeight: '600' }}
          >
            External Currency Requests
          </Typography>
          {isPending ? (
            <div></div>
          ) : !totalCount ? (
            <div style={{ width: 20, height: 20, alignItems: 'center' }}>
              <SVGInline svg={checkCircleIcon} />
            </div>
          ) : (
            <span
              style={{
                background: '#F5F5F5',
                borderRadius: 8,
                padding: '4px 8px',
                fontWeight: 500,
                fontSize: 13,
                color: '#1F1F1F',
                marginLeft: 0,
                display: 'inline-block'
              }}
            >
              {overdueCount}/{totalCount}
              {totalCount === 25 ? '+' : ''}
            </span>
          )}
        </div>
        <div
          style={{ maxHeight: 360, overflowY: 'auto', padding: 0, margin: 0 }}
        >
          {isPending ? (
            <Typography style={{ marginLeft: 16 }}>Loading...</Typography>
          ) : !externalCurrencyRequests?.length ? (
            <AllDone />
          ) : (
            externalCurrencyRequestsAsWidgetFormat.map(
              (externalCurrencyRequest, idx) => (
                <WidgetListItem
                  key={externalCurrencyRequest.id}
                  createdAt={externalCurrencyRequest.createdAt}
                  name={externalCurrencyRequest.name}
                  currencyType={externalCurrencyRequest.currencyTypeLabel}
                  overdueDays={externalCurrencyRequest.overdueDays}
                  onButtonClick={() => onItemClick(externalCurrencyRequest)}
                  disableDivider={
                    idx === externalCurrencyRequestsAsWidgetFormat.length - 1
                  }
                  warningThreshold={institute?.point_request_warning_days || 7}
                  overdueThreshold={institute?.point_request_overdue_days || 14}
                />
              )
            )
          )}
        </div>
      </Paper>
      <ExternalCurrencyModal
        open={externalCurrencyModalOpen}
        onClose={handleCloseExternalCurrencyModal}
        externalCurrency={selectedExternalCurrency}
        institute={institute}
      />
    </>
  );
};

export default ExternalCurrencyWidget;
